import { getSharedWorkControllerFetchSharedWorkQueryOptions } from '@/api/generated/okami.ts'
import { NotFound } from '@/components/layouts/404.tsx'
import { Avatar, AvatarImage } from '@/components/ui/avatar.tsx'
import { Badge } from '@/components/ui/badge.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Card } from '@/components/ui/card.tsx'
import { createDomainUrlWithPath, getTagColor } from '@/utils/helpers.ts'
import { createFileRoute } from '@tanstack/react-router'
import { getYear } from 'date-fns'
import { BookOpen, Share2, Tv2 } from 'lucide-react'
import { toast } from 'sonner'

export const Route = createFileRoute('/public/share/$sharedWorkId')({
  component: ShareWork,
  loader: async ({ context, params }) => {
    return context.queryClient.ensureQueryData(
      getSharedWorkControllerFetchSharedWorkQueryOptions(params.sharedWorkId),
    )
  },
  head: ({ loaderData }) => {
    const work = loaderData?.work
    return {
      meta: [
        {
          title: `${work?.name} ${work?.alternativeName ? `[${work?.alternativeName}]` : ''}`,
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          property: 'og:title',
          name: 'description',
          content: loaderData?.work.description,
        },

        {
          property: 'og:image',
          content: loaderData?.work?.imageUrl,
        },
        {
          property: 'og:image:alt',
          content: `Capa de ${work?.name} [${work?.alternativeName}]`,
        },
        {
          property: 'twitter:card',
          content: 'summary',
        },
        {
          property: 'twitter:title',
          content: work?.name,
        },
        {
          property: 'twitter:image',
          content: work?.imageUrl,
        },
        {
          property: 'twitter:image:alt',
          content: `Capa de ${work?.name} [${work?.alternativeName}]`,
        },
      ],
    }
  },
  notFoundComponent: NotFound,
})

function ShareWork() {
  const { sharedWorkId } = Route.useParams()
  const { work, owner } = Route.useLoaderData()

  function handleOpenUrl() {
    window.open(work.url, '_blank')
  }

  async function handleShareWork() {
    if (!sharedWorkId) {
      toast.error('Obra não encontrada')
      return
    }

    await navigator.clipboard.writeText(createDomainUrlWithPath(sharedWorkId))

    toast.success('Link copiado para sua  área de transferência')
  }

  const workPredicate = work.category === 'ANIME' ? 'Assistir' : 'Ler'

  return (
    <div className="container max-w-[1200px] mx-auto px-4 py-8 h-screen items-center justify-center">
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-end">
          <div className="flex flex-col gap-3 justify-center items-center">
            <div className="flex items-center gap-2">
              <div className="flex-col items-end hidden md:flex">
                <span className="text-foreground text-md">{owner.name}</span>
                <p className="text-sm text-muted-foreground">Membro de desde {getYear(owner.createdAt)}</p>
              </div>

              <Avatar className="size-12">
                <AvatarImage src={owner.imageUrl ?? '/okami-logo.png'} />
              </Avatar>
            </div>
          </div>
        </div>
      </div>

      <div className="flex  flex-col md:flex-row gap-8 ">
        <div className="flex-shrink-0">
          <Card className="w-full md:w-64 overflow-hidden border-none bg-transparent">
            <img
              src={work.imageUrl}
              alt={`Capa de ${work.name} [${work.alternativeName}]`}
              className="rounded-lg object-cover size-full h-[420px]"
              loading="eager"
            />
          </Card>
        </div>

        <div className="flex-grow">
          <div className="flex flex-col gap-4">
            <div>
              <h1 className="text-3xl font-bold">{work.name}</h1>
              <p className="text-gray-400 mt-1">{work.alternativeName}</p>
            </div>

            <div className="flex flex-wrap gap-2">
              {work.tags.map((tag) => {
                const color = getTagColor(tag.color)
                const bgColor = getTagColor(tag.color, 800)

                return (
                  <Badge
                    key={tag.id}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = bgColor)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = color)}
                    style={{ background: color }}
                    variant="secondary"
                    className={"text-gray-100 hover:bg-['red']"}
                  >
                    {tag.name}
                  </Badge>
                )
              })}
            </div>

            <div className="mt-2">
              <h2 className="text-md mb-2 text-muted-foreground">Sinopse</h2>
              <p className="text-gray-300 leading-relaxed">{work.description}</p>
            </div>

            <div className="flex flex-wrap gap-4 mt-4 mb-4">
              <Button className="bg-purple-700 hover:bg-purple-600 text-gray-200" onClick={handleOpenUrl}>
                {work.category === 'ANIME' ? (
                  <Tv2 className="mr-2  size-4" />
                ) : (
                  <BookOpen className="mr-2 size-4" />
                )}
                Começar a {workPredicate}
              </Button>
              <Button
                onClick={handleShareWork}
                variant="outline"
                className="border-gray-700 text-gray-300 hover:bg-gray-800"
              >
                <Share2 className="mr-2 h-4 w-4" />
                Compartilhar
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-[#1e2832] mt-12">
        <div className="container mx-auto px-4 py-6">
          <p className="text-center text-gray-500 text-sm">
            Obra compartilhada • Visualização somente leitura
          </p>
          <p className="text-center text-gray-500 text-sm mt-2">
            By{' '}
            <a href="https://go.myokami.xyz" className="font-bold underline">
              Okami
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
